/**
 * @ccheng/ui 共用樣式
 * 不依賴消費端（web/admin）的 Tailwind pipeline —— packages/ui 透過 npm workspace
 * symlink 進 node_modules，Tailwind v4 的自動內容偵測不保證掃到這裡，故一律用
 * 純 CSS（class 前綴 cui-）並在元件掛載時注入一份共用 <style>，避免樣式漏失。
 *
 * ── 2026-09-02 視覺翻新（業主核定「品牌金」方案）─────────────────────────
 * 規範文件：ccheng/migration/docs/後台UI視覺翻新規範.md
 *
 * 主色由藍改金。原因：舊值 #2563eb 恰等於前台 custom_new.css 的 --clr-info-700
 * （資訊色），也就是後台一直把「資訊色」當品牌色用；前台真正的品牌色是金色系統
 * （--clr-primary-*）。本次把金色帶進後台當 primary，藍色回歸 --cui-info 語意角色。
 *
 * 同批修掉三處實測未達 WCAG AA（4.5:1）的色彩：
 *   表頭／neutral badge #64748b on #f1f5f9 = 4.34:1 → 新增 --cui-text-secondary #475569（6.9:1）
 *   成功色 #16a34a on #f0fdf4 = 3.15:1 → #15803d on #dcfce7（4.57:1）
 *   危險色 #dc2626 on #fef2f2 = 4.42:1 → #b91c1c on #fef2f2（5.92:1）
 *
 * CJK 字重限制：Microsoft JhengHei 只有 400/700，Noto Sans TC 無 600。原本
 * badge/tab/modal-title 用的 600 在 Windows 中文會被瀏覽器就近替代，粗細不可控，
 * 故中文承載元素一律只用 400 / 500 / 700。
 *
 * ⚠️ 本次只動視覺：選單詞條、欄位集合、資訊架構一字未改（驗證基準以此比對舊站）。
 */

export const CUI_STYLE_TAG_ID = "ccheng-ui-base-styles";

/**
 * 深色調色盤。三個 selector 共用同一份 token，故抽成常數而不是抄三遍
 * ——抄三遍必然會有一天只改到其中一份。
 *
 * 為什麼需要三段：
 *   1. `@media (prefers-color-scheme: dark) { :root:not([data-theme="light"]) }`
 *      使用者沒有明確選過主題時，跟隨系統。`:not([data-theme="light"])` 讓
 *      「系統是深色但使用者選了淺色」時淺色勝出。
 *   2. `:root[data-theme="dark"]`
 *      使用者明確選了深色 —— 即使系統是淺色也要深。
 * 少了任何一段，切換鈕就只有單向有效（常見的 bug：系統淺色時切不到深色）。
 */
const DARK_PALETTE = `
    /*
     * ⚠️ 深色的層次方向與淺色**相反**，不能只是把淺色的值反過來抄。
     * 淺色：頁面底 #f8fafc（灰） < 卡片 #fff（白） —— 卡片比頁面亮，所以浮起來。
     * 深色若照抄成「bg 最暗、bg-subtle 較亮」，卡片就變成比頁面更暗＝凹陷下去，
     * 陰影也失去意義（深色底上的陰影本來就幾乎看不見，全靠亮度差建立層次）。
     * 故深色改為：頁面底最暗，往上每一層漸亮。
     */
    --cui-bg: #161b24;          /* 卡片／彈窗：最亮，浮在最上層 */
    --cui-bg-subtle: #0d1117;   /* 頁面底：最暗 */
    --cui-bg-muted: #1e2430;    /* hover／badge 底：比卡片再亮一階 */
    --cui-bg-header: #232a38;   /* 表頭：最亮，維持三階色彩的最上層 */
    --cui-border: #2b3342;
    --cui-border-strong: #3c4658;
    /* 深色卡片底 #161b24 上要達 3:1 需要更亮的灰；#3c4658 只有 1.82:1 */
    --cui-border-control: #626f82;   /* 對 #161b24 = 3.38:1 ✓ */
    --cui-text: #e5e9f0;
    --cui-text-secondary: #b6c0ce;
    --cui-text-muted: #94a3b8;
    /*
     * 2026-09-04 B3 瀏覽器稽核（C2/C4/C5/C6）：admin 端一批 hint／稽核／單選副說明
     * 文字（course-management、course-setting 等頁）直接吃 var(--cui-text-subtle)
     * 這顆 token（不在 packages/ui 檔案領域內，無法逐一改成 muted），原值 #64748b
     * 對深色卡片底只有 3.63:1，不到 AA 4.5:1。改成與 --cui-text-muted 同值
     * （#94a3b8 對 #161b24 = 6.73:1 ✓），讓所有引用這顆 token 的文字（無論在哪個
     * 套件）一次達標，不必逐檔搬字。disabled/裝飾性用途因此也變亮，但那類用途本來
     * 就是 WCAG 豁免，變亮不是回歸。
     */
    --cui-text-subtle: #94a3b8;
    /* 深色底下 brand-800 太暗濁，主色改採 brand-400／200 亮階 */
    --cui-primary: #f1cd86;
    --cui-primary-hover: #ffedca;
    --cui-primary-contrast: #3d2b06;
    --cui-danger: #f87171;
    --cui-danger-bg: #2a1214;
    /*
     * 深色實心紅。不能沿用淺色的 #b91c1c —— 它對深色 modal 底 #161b24 只有
     * 2.67:1，按鈕整塊會融進背景（SC 1.4.11 要求非文字對比 3:1）。
     * #dc2626：白字 4.83:1 ✓、對 modal 底 3.57:1 ✓，兩個標準同時滿足。
     */
    --cui-danger-solid: #dc2626;
    --cui-success: #4ade80;
    --cui-success-bg: #0f2417;
    --cui-warning: #fbbf24;
    --cui-warning-bg: #2a1e08;
    --cui-info: #60a5fa;
    --cui-info-bg: #0f1e3d;
    --cui-neutral-bg: #1e2430;
    /* 斑馬紋在深色下是「比卡片略亮」而非略暗，方向同上 */
    --cui-stripe: #1b212c;
    /*
     * brand-50/100/200 在淺色模式是「極淺金底」（列 hover、link 鈕 hover 底）。
     * 深色模式下若沿用原值，會變成亮金色塊配亮文字，完全不可讀。
     * 這裡把低階數重新映射成「深色底上的極淡金染色」，維持同樣的語意
     * （越低階＝越接近背景），而不是同樣的亮度。
     */
    --cui-brand-50: #1a1710;
    --cui-brand-100: #221d14;
    --cui-brand-200: #2e2718;
    --cui-brand-300: #4a3d22;
    /* 400~900 在深色底上作為文字/強調色，維持原值（亮階，對深底對比足夠） */
    /*
     * 側欄本來就是暖黑，深色模式下會與內容區糊在一起。內容區改用偏冷的深藍灰
     * （上面的 --cui-bg-*），與側欄的暖黑形成色溫差；再把側欄底再壓深一階，
     * 讓兩者的邊界靠明度差而非邊框線就看得出來。
     */
    --cui-sidebar-bg: #100d09;
    --cui-sidebar-border: rgba(255,255,255,.08);
    /*
     * focus ring 原值 rgba(134,97,25,.35) 是從 brand-800 來的，疊在深色底上會變成
     * #362c18 —— 一圈幾乎融進背景的濁棕，鍵盤操作者看不到自己在哪。改用亮金並提高
     * alpha。這顆先前完全沒被深色區塊覆寫過。
     */
    --cui-focus-ring: rgba(241, 205, 134, .45);
    --cui-sidebar-shadow: 4px 0 24px rgba(0, 0, 0, .45);
    color-scheme: dark;`;

const DARK_TOKENS_CSS = `@media (prefers-color-scheme: dark) {
  :root:not([data-theme="light"]) {${DARK_PALETTE}
  }
}
:root[data-theme="dark"] {${DARK_PALETTE}
}`;

export const BASE_CSS = `
.cui-reset { box-sizing: border-box; font-family: var(--cui-font-sans); }
.cui-reset *, .cui-reset *::before, .cui-reset *::after { box-sizing: inherit; }

:root {
  /* ── 字族 ───────────────────────────────────────────────
     明確把 PingFang TC / Microsoft JhengHei 放在最前，不再倚賴瀏覽器隱式
     字符替代，確保 Mac / Windows 的中文字重渲染一致。 */
  --cui-font-sans: "PingFang TC", "Microsoft JhengHei", "Noto Sans TC", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  --cui-font-mono: ui-monospace, "SF Mono", "Cascadia Code", Consolas, monospace;

  /* ── 品牌金（取自前台 gold 系統）──────────────────────────
     只有 800/900 兩階對白字達 AA，可承載小字；600/700 僅供 icon 與大字。 */
  --cui-brand-50:  #fffcf7;
  --cui-brand-100: #fff8e9;
  --cui-brand-200: #ffedca;
  --cui-brand-300: #fee3ad;
  --cui-brand-400: #f1cd86;
  --cui-brand-500: #d6af62;
  --cui-brand-600: #b99345;
  --cui-brand-700: #9c762c;
  --cui-brand-800: #866119;  /* 對白字 5.62:1 ✓ AA */
  --cui-brand-900: #725212;

  /* ── 中性 ─────────────────────────────────────────────── */
  --cui-bg: #ffffff;
  --cui-bg-subtle: #f8fafc;
  --cui-bg-muted: #f1f5f9;
  --cui-bg-header: #eef2f7;      /* 表頭：比 bg-muted 再深一階，做三階色彩最上層 */
  --cui-border: #e2e8f0;         /* 裝飾性分隔線（表格列、卡片邊）—— 不受 WCAG 1.4.11 管 */
  --cui-border-strong: #cbd5e1;  /* 較明顯的分隔線；⚠️ 對白只有 1.48:1，**不足以當輸入框框線** */
  /*
   * 互動控制項的框線（輸入框、下拉、textarea、核取方塊）。
   *
   * WCAG 2.1 SC 1.4.11「非文字對比」要求「識別使用者介面元件所需的視覺資訊」
   * 對相鄰色至少 **3:1** —— 表單欄位的邊界正是這一條的典型對象。
   *
   * 實測（相對亮度公式）：
   *   --cui-border        #e2e8f0 對白 1.23:1  ✗
   *   --cui-border-strong #cbd5e1 對白 1.48:1  ✗   ← 連「strong」都差很遠
   *   本 token            #8592a3 對白 3.16:1  ✓
   *
   * 業主 2026-09-03 回報「框線超級不清楚，不論黑底白底都不清楚」，實測證實兩種
   * 主題都遠低於標準（淺色 1.23:1、深色 1.36:1）。這不是主觀偏好問題。
   */
  --cui-border-control: #8592a3;
  --cui-text: #0f172a;
  --cui-text-secondary: #475569; /* 淺灰底上的次要文字（表頭、neutral badge）：對 bg-muted 6.9:1 ✓ */
  --cui-text-muted: #64748b;     /* 純白底上的輔助文字：對白 4.76:1 ✓（不可疊灰底） */
  /*
   * 2026-09-04 B3 瀏覽器稽核（C2/C4/C5/C6）：原值 #94a3b8 對白只有 2.56:1、對
   * 表頭底 2.28:1，被拿去當 hint／稽核文字／單選副說明這些「真的要讀」的文字用
   * （多半在 admin 端以 var(--cui-text-subtle) 直接引用，不在本套件檔案內，無法
   * 逐一改配色）。改成與 --cui-text-muted 同值（#64748b 對白 4.76:1 ✓），讓引用
   * 這顆 token 的地方一次達 AA。disabled 狀態本來就 WCAG 豁免，變深不是回歸；
   * 真正需要更淡（裝飾性、非文字）的地方改吃 --cui-text-muted 明確標示語意
   * （見 .cui-input::placeholder／.cui-dt-search::placeholder；.cui-dt-sort-icon
   * 2026-09-04 B3 第 3 輪②再改為 text-secondary，見該規則旁註解）。
   */
  --cui-text-subtle: #64748b;

  /* ── 語意 ─────────────────────────────────────────────── */
  --cui-primary: var(--cui-brand-800);
  --cui-primary-hover: var(--cui-brand-900);
  --cui-primary-contrast: #ffffff;
  --cui-danger: #b91c1c;
  --cui-danger-bg: #fef2f2;
  /*
   * 實心紅底（白字）專用，與 --cui-danger 分開。
   *
   * 為什麼不能共用：深色模式把 --cui-danger 改指到 #f87171，那顆是調亮給
   * 「紅字疊在深色底上」用的（ghost/link 的刪除文字，實測 6.24:1 ✓）。
   * 但白字疊 #f87171 只有 **2.77:1**，遠低於 AA 4.5:1 —— 同一顆 token 被兩種
   * 相反的用途借用，必然有一邊不合格。
   */
  --cui-danger-solid: #b91c1c;   /* 白字 6.47:1 ✓ */
  --cui-success: #15803d;
  --cui-success-bg: #dcfce7;
  --cui-warning: #92400e;
  --cui-warning-bg: #fffbeb;
  --cui-info: #2563eb;           /* 藍色回歸資訊色角色（與前台 --clr-info-700 同值） */
  --cui-info-bg: #eff6ff;
  --cui-neutral-bg: #f1f5f9;
  --cui-stripe: #f5f7fa;         /* 斑馬紋：較舊值 #f8fafc 加大對比 */

  /* ── 側欄專用（暖黑 + 金，與內容區不共用底色）────────────── */
  --cui-sidebar-bg: #17130e;
  --cui-sidebar-border: rgba(255, 255, 255, .06);
  --cui-sidebar-text: #cbc3b2;            /* 對底 12.3:1 ✓ */
  --cui-sidebar-text-muted: #8a8272;      /* 對底 4.95:1 ✓ */
  --cui-sidebar-hover-bg: #241d15;
  --cui-sidebar-active-bg: rgba(214, 175, 98, .14);
  --cui-sidebar-active-text: #f1cd86;     /* 對底 12.4:1 ✓ */
  --cui-sidebar-active-border: #d6af62;
  --cui-sidebar-brand-text: #ffffff;
  --cui-sidebar-shadow: none;
  /*
   * 側欄搜尋高亮的文字色。刻意**不**用 --cui-brand-300：那顆在深色調色盤被重新
   * 映射成 #4a3d22（內容區 hover 用的深色底），而側欄的配色是凍結的、不隨主題變。
   * 實測若沿用 brand-300，深色模式下高亮底 #4b3e24 疊文字 #4a3d22＝對比 1.02:1，
   * 字等於消失。這顆只在 :root 定義一次，DARK_PALETTE 不得覆寫。
   */
  --cui-sidebar-mark-text: #fee3ad;   /* 對高亮底 8.35:1 ✓ */

  /* ── 間距（4px 網格）───────────────────────────────────── */
  --cui-space-1: 4px;  --cui-space-2: 8px;  --cui-space-3: 12px;
  --cui-space-4: 16px; --cui-space-5: 20px; --cui-space-6: 24px;
  --cui-space-8: 32px; --cui-space-10: 40px; --cui-space-12: 48px;

  /* ── 字級 ─────────────────────────────────────────────── */
  --cui-text-2xl: 20px; --cui-text-xl: 18px;  --cui-text-lg: 15px;
  --cui-text-md: 14px;  --cui-text-base: 13px; --cui-text-sm: 12px;
  --cui-text-xs: 11px;

  /* ── 圓角 ─────────────────────────────────────────────── */
  --cui-radius-sm: 4px; --cui-radius-md: 6px; --cui-radius-lg: 8px;
  --cui-radius-xl: 12px; --cui-radius-full: 999px;

  /* ── 效果 ─────────────────────────────────────────────── */
  /* 焦點環：疊白必須 ≥3:1（WCAG 2.4.11／1.4.11——焦點指示器本身就是非文字 UI 元件）。
     原本 0.35 實測只有 1.66:1，全站 70 頁的按鈕焦點環都不合格；0.75 實測 3.36:1。
     視覺上會比原本明顯——那正是焦點環該有的樣子，它不是裝飾。
     深色的 rgba(241,205,134,.45) 實測 3.31–3.35:1 已通過，不動。 */
  --cui-focus-ring: rgba(134, 97, 25, 0.75);
  --cui-shadow-sm: 0 2px 8px rgba(15, 23, 42, 0.08);
  --cui-shadow: 0 1px 2px rgba(15, 23, 42, 0.06), 0 1px 1px rgba(15, 23, 42, 0.04);
  --cui-shadow-lg: 0 10px 30px rgba(15, 23, 42, 0.18);
  color-scheme: light;
  /*
   * 核取方塊／單選鈕沿用瀏覽器原生控制項（可及性與各平台操作手感最好），但預設
   * 勾選色是系統藍，主色換金之後會變成畫面上唯一一抹藍。accent-color 只換顏色、
   * 不改行為，是把原生控制項納入品牌最省事的做法。
   */
  accent-color: var(--cui-primary);
}
${DARK_TOKENS_CSS}

/* ---------- Button ---------- */
.cui-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  border-radius: var(--cui-radius-md); font-size: var(--cui-text-base); font-weight: 500; line-height: 1;
  padding: var(--cui-space-2) var(--cui-space-3); border: 1px solid transparent; cursor: pointer;
  font-family: var(--cui-font-sans);
  transition: background-color .12s, border-color .12s, opacity .12s;
  white-space: nowrap;
}
.cui-btn:disabled { opacity: .5; cursor: not-allowed; }
.cui-btn:focus-visible { outline: 2px solid var(--cui-focus-ring); outline-offset: 1px; }
.cui-btn-primary { background: var(--cui-primary); color: var(--cui-primary-contrast); }
.cui-btn-primary:hover:not(:disabled) { background: var(--cui-primary-hover); }
.cui-btn-secondary { background: var(--cui-bg); color: var(--cui-text); border-color: var(--cui-border-control); }
.cui-btn-secondary:hover:not(:disabled) { background: var(--cui-bg-muted); }
/*
 * 危險按鈕改為 ghost：常態只有紅字，hover 才浮現淺紅底。
 * 原本實心紅底在清單頁一次出現 20 個，視覺權重壓過主 CTA，且紅色重複出現會讓
 * 使用者的警覺遞減——真正需要警覺時（批次刪除確認）反而失效。把危險感延遲到
 * 滑鼠靠近才出現。實心紅底保留給 .cui-btn-danger-solid（確認對話框的最終按鈕）。
 */
.cui-btn-danger { background: transparent; color: var(--cui-danger); }
.cui-btn-danger:hover:not(:disabled) { background: var(--cui-danger-bg); }
.cui-btn-danger-solid { background: var(--cui-danger-solid); color: #fff; }
.cui-btn-danger-solid:hover:not(:disabled) { opacity: .9; }
.cui-btn-ghost { background: transparent; color: var(--cui-text); }
.cui-btn-ghost:hover:not(:disabled) { background: var(--cui-bg-muted); }
/* 動作欄的「編輯」：無框主色文字，與同列 ghost 的「刪除」視覺重量一致 */
.cui-btn-link { background: transparent; color: var(--cui-primary); }
.cui-btn-link:hover:not(:disabled) { background: var(--cui-brand-100); }
.cui-btn-sm { padding: var(--cui-space-1) var(--cui-space-2); font-size: var(--cui-text-sm); }

/* ---------- 表單輸入 ----------
 * 抽成共用 class 的原因：inline style 表達不了 :focus。登入頁原本用 inline
 * style 並寫了 outline:none，卻沒有任何替代的焦點樣式 —— 實測 focus 後
 * border 與未 focus 完全相同、box-shadow 為 none，鍵盤操作者完全看不出游標在哪。
 * 這是可及性缺陷，不是視覺偏好。
 */
.cui-input {
  width: 100%; box-sizing: border-box;
  border: 1px solid var(--cui-border-control); border-radius: var(--cui-radius-sm);
  padding: 8px 10px; font-size: var(--cui-text-base); font-family: var(--cui-font-sans);
  background: var(--cui-bg); color: var(--cui-text); line-height: 1.4;
}
/*
 * C3（B3 稽核）：placeholder 也是文字（WCAG 1.4.3），原本吃 text-subtle 在淺色
 * 只有 2.56:1。改吃 text-muted：對白 4.76:1（淺）／對深色卡片底 6.73:1（深）✓。
 */
.cui-input::placeholder { color: var(--cui-text-muted); }
.cui-input:focus {
  outline: none;                      /* 用自訂的 ring 取代原生 outline，不是取消焦點提示 */
  border-color: var(--cui-primary);
  box-shadow: 0 0 0 3px var(--cui-focus-ring);
}
/* 驗證未過：框線轉紅 + 淡紅光暈。與 focus ring 同一套機制，只換色。 */
.cui-input-invalid { border-color: var(--cui-danger); }
.cui-input-invalid:focus { border-color: var(--cui-danger); box-shadow: 0 0 0 3px rgba(185, 28, 28, .18); }
.cui-input:disabled { background: var(--cui-bg-muted); color: var(--cui-text-subtle); cursor: not-allowed; }
/* 多行輸入：疊在 .cui-input 之上只加高度行為與等寬字。
   等寬是刻意的——這些 textarea 裝的是 JSON／HTML／網址清單這類要對齊看的內容。 */
.cui-textarea { resize: vertical; min-height: 72px; font-family: var(--cui-font-mono); }
.cui-field { display: flex; flex-direction: column; gap: var(--cui-space-1); font-size: var(--cui-text-base); }
.cui-field-label { font-weight: 500; color: var(--cui-text-secondary); }

/* ---------- 原生表單元素的焦點安全網 ----------
   有 12 個頁面仍靠 admin/src/lib/trainingVideo/formStyles.ts 的 CSSProperties
   常數上樣式。inline style **物理上寫不出 :focus**——這是本次無障礙盤點裡
   「8 頁鍵盤焦點完全不可見」的單一根因，不是誰忘了加。

   那 12 頁會逐批改掛 .cui-input，但在那之前鍵盤使用者不該看不見自己在哪。
   所以這裡對「沒有掛 .cui-* class 的原生表單元素」補一道 focus-visible。
   :not() 讓已遷移的元素完全不受影響（.cui-input:focus 有自己的 ring），
   遷移完成後這段仍然無害——它只在有人漏掛 class 時才生效。
   用 :focus-visible 而非 :focus：滑鼠點擊不該冒出焦點框。 */
input:not([type="checkbox"]):not([type="radio"]):not(.cui-input):focus-visible,
select:not(.cui-input):focus-visible,
textarea:not(.cui-input):focus-visible {
  outline: 2px solid var(--cui-primary);
  outline-offset: 1px;
  border-color: var(--cui-primary);
}
/* 勾選框與單選鈕的外框要留在元件外側，不然會被自身的方框吃掉 */
input[type="checkbox"]:focus-visible, input[type="radio"]:focus-visible {
  outline: 2px solid var(--cui-primary); outline-offset: 2px;
}

/* ---------- 核取方塊 + 標籤的行內組合 ----------
   「顯示被删除」這類開關，全站原本至少三種 inline 寫法（CmsListPage、
   NotifyTemplateListPage、course-setting/region）。收成一個 class 的重點不只是
   統一外觀：inline style 寫不出 :hover 與 :focus-within，所以那些開關在
   鍵盤操作下沒有任何「我正停在這裡」的提示。
   （註：本檔是 template literal，註解裡不能出現反引號，會提前終止字串。） */
.cui-checkbox-label {
  display: inline-flex; align-items: center; gap: var(--cui-space-2);
  font-size: var(--cui-text-base); color: var(--cui-text-secondary);
  cursor: pointer; user-select: none;
  padding: var(--cui-space-1) var(--cui-space-2);
  border-radius: var(--cui-radius-sm);
}
.cui-checkbox-label:hover { background: var(--cui-bg-muted); color: var(--cui-text); }
/* 焦點落在裡面的 input 時整塊給提示——focus ring 畫在 12px 的方框上很難看見 */
.cui-checkbox-label:focus-within { background: var(--cui-bg-muted); color: var(--cui-text); }
.cui-checkbox-label input { margin: 0; cursor: pointer; }
/* 工具列上與其他控制項並排的開關：多給左右間距，不要和按鈕黏在一起 */
.cui-toggle-inline { margin-inline-start: var(--cui-space-2); }

/* ---------- 內文連結 ----------
   .cui-btn-link 是「長得像連結的按鈕」（有 padding、hover 底色）。
   這一個是真的 <a>：不加底色、永遠帶底線。底線不是裝飾——
   只用顏色標示連結，色覺辨認有困難的人看不出哪裡可以點（WCAG 1.4.1）。 */
.cui-link {
  color: var(--cui-primary); text-decoration: underline;
  text-underline-offset: 2px; border-radius: 2px;
}
.cui-link:hover { color: var(--cui-brand-800); }
.cui-link:focus-visible { outline: 2px solid var(--cui-focus-ring); outline-offset: 2px; }

/* ---------- 唯讀鍵值對 ----------
   語意用 <dl>/<dt>/<dd>：那是「名稱→值」的既有 HTML 結構，
   讀屏會成對播報。用兩個 <div> 排版的話，值和名稱之間沒有任何關聯。 */
.cui-kv { display: grid; grid-template-columns: minmax(120px, max-content) 1fr; gap: var(--cui-space-2) var(--cui-space-3); margin: 0; }
.cui-kv dt { color: var(--cui-text-secondary); font-weight: 500; }
.cui-kv dd { margin: 0; color: var(--cui-text); word-break: break-word; }

/* ---------- 唯讀程式碼／JSON 區塊 ----------
   先前是借 textarea 的樣式來顯示唯讀內容：外觀像可編輯，實際不能改。 */
.cui-code-block {
  margin: 0; padding: var(--cui-space-3); overflow-x: auto;
  background: var(--cui-bg-muted); border: 1px solid var(--cui-border);
  border-radius: var(--cui-radius-sm);
  font-family: var(--cui-font-mono); font-size: var(--cui-text-sm);
  color: var(--cui-text); white-space: pre-wrap; word-break: break-word;
}

/* ---------- 工具列小按鈕（RichTextEditor 之類） ----------
   不用 .cui-btn：那是 32px 高的表單按鈕，塞進編輯器工具列會過大。
   active 態同時給底色與框線，不單靠顏色傳達狀態（色弱可辨）。 */
.cui-toolbar-btn {
  border: 1px solid var(--cui-border-control); background: var(--cui-bg); color: var(--cui-text);
  border-radius: var(--cui-radius-sm); font-size: var(--cui-text-sm); font-family: var(--cui-font-sans);
  padding: 3px 8px; cursor: pointer; line-height: 1.4;
}
.cui-toolbar-btn:hover { background: var(--cui-bg-muted); }
.cui-toolbar-btn:focus-visible { outline: 2px solid var(--cui-focus-ring); outline-offset: 1px; }
.cui-toolbar-btn[aria-pressed="true"] {
  background: var(--cui-primary); border-color: var(--cui-primary); color: var(--cui-primary-contrast);
}

/* ---------- 語系分頁（CMS 表單的 zh/en 切換） ----------
   刻意不共用 .cui-tab：這組是「與下方卡片接合」的資料夾頁籤造型
   （底邊與卡片同色以消除接縫），.cui-tab 是底線式，兩者形狀不同。
   font-weight 只用 400/700——600 在 Windows 中文不可控
   （JhengHei 只有 400/700、Noto Sans TC 無 600，會被合成或退回）。 */
.cui-locale-tab {
  border: 1px solid transparent; border-bottom: 1px solid transparent; margin-bottom: -1px;
  border-radius: 8px 8px 0 0; background: transparent; color: var(--cui-text-muted);
  font-weight: 400; font-size: var(--cui-text-sm); font-family: var(--cui-font-sans);
  padding: 6px 14px; cursor: pointer;
}
.cui-locale-tab:hover { color: var(--cui-text); }
.cui-locale-tab:focus-visible { outline: 2px solid var(--cui-focus-ring); outline-offset: -2px; }
.cui-locale-tab[aria-selected="true"] {
  border-color: var(--cui-border); border-bottom-color: var(--cui-bg);
  background: var(--cui-bg); color: var(--cui-primary); font-weight: 700;
}

/* ---------- Badge ----------
   每個語意除了顏色還帶不同形狀的圓點：實心圓＝成功、空心圓＝中性、方塊＝警告、
   紅空心＝危險。色弱使用者不必單靠顏色分辨。info 刻意不加圓點——它承載的是
   「分類標籤」而非「狀態」，形狀差異讓兩者一眼可分。 */
.cui-badge {
  display: inline-flex; align-items: center; gap: 5px; border-radius: var(--cui-radius-full);
  font-size: 11.5px; font-weight: 700; padding: 2px 8px; line-height: 1.6;
  white-space: nowrap;
}
.cui-badge::before { content: ""; width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
.cui-badge-neutral { background: var(--cui-neutral-bg); color: var(--cui-text-secondary); }
.cui-badge-neutral::before { border: 1.5px solid var(--cui-text-secondary); }
.cui-badge-success { background: var(--cui-success-bg); color: var(--cui-success); }
.cui-badge-success::before { background: var(--cui-success); }
.cui-badge-warning { background: var(--cui-warning-bg); color: var(--cui-warning); }
.cui-badge-warning::before { background: var(--cui-warning); border-radius: 1px; transform: rotate(45deg); }
.cui-badge-danger { background: var(--cui-danger-bg); color: var(--cui-danger); }
.cui-badge-danger::before { border: 1.5px solid var(--cui-danger); }
/* info 綁 --cui-info 而非 --cui-primary：主色 swap 成金之後，資訊 badge 必須留在藍色 */
.cui-badge-info { background: var(--cui-info-bg); color: var(--cui-info); }
/* 分類標籤不掛狀態圓點（info 天生是標籤用；其餘 variant 由 dot={false} 指定） */
.cui-badge-info::before, .cui-badge-nodot::before { display: none; }
.cui-badge-nodot { gap: 0; }

/* ---------- 通道徽章 ----------
   四種資料通道（S 級 / C 級 / IDX / BQ）的篩選與分頁能力天差地遠：S 級有總筆數、
   可跳頁；IDX 兩者皆無。現況沒有任何提示，使用者要到第 27 個頁面才第一次撞見
   差異，容易誤判為 bug。固定顯示在頁面標題旁，進頁面第一秒就講清楚。
   ⚠️ IDX 刻意用中性灰而非藍色，避免使用者誤以為它跟 S 級一樣強大。 */
.cui-channel {
  display: inline-flex; align-items: center; gap: 5px; height: 22px; padding: 0 9px;
  border-radius: var(--cui-radius-full); font-size: 11.5px; font-weight: 700; white-space: nowrap;
}
.cui-channel-s   { background: var(--cui-info-bg); color: var(--cui-info); }
.cui-channel-c   { background: var(--cui-warning-bg); color: var(--cui-warning); }
.cui-channel-idx { background: var(--cui-bg-muted); color: var(--cui-text-secondary); }
.cui-channel-bq  { background: var(--cui-bg-muted); color: var(--cui-text-secondary); }

/* ---------- Tabs ---------- */
.cui-tabs-list { display: flex; gap: 2px; border-bottom: 1px solid var(--cui-border); }
.cui-tab {
  padding: var(--cui-space-2) 14px; font-size: var(--cui-text-base); font-weight: 500; color: var(--cui-text-muted);
  background: transparent; border: none; border-bottom: 2px solid transparent;
  cursor: pointer; margin-bottom: -1px; font-family: var(--cui-font-sans);
}
.cui-tab:hover { color: var(--cui-text); }
.cui-tab[aria-selected="true"] { color: var(--cui-primary); border-bottom-color: var(--cui-primary); font-weight: 700; }
.cui-tab:focus-visible { outline: 2px solid var(--cui-focus-ring); outline-offset: -2px; }
.cui-tab-panel { padding-top: var(--cui-space-3); }

/* ---------- Modal ---------- */
.cui-modal-overlay {
  position: fixed; inset: 0; background: rgba(15, 23, 42, 0.5);
  display: flex; align-items: center; justify-content: center; padding: var(--cui-space-4); z-index: 1000;
  animation: cui-overlay-in .15s ease-out;
}
/*
 * 進場動畫。業主要求「不要用類似 alert 方式跳出」——動畫是這個要求裡最容易漏掉
 * 的一環：window.confirm 是瞬間阻斷式彈出，若自製 Modal 仍是硬切 mount/unmount，
 * 視覺上跟 alert 沒有本質差異，只是換了容器。節奏對齊既有的 cui-toast-in（150ms
 * ease-out），讓全站的浮層是同一套動畫語言。
 */
@keyframes cui-overlay-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes cui-modal-in {
  from { opacity: 0; transform: translateY(6px) scale(.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
/* 尊重使用者的「減少動態效果」設定——前庭功能敏感者會因位移動畫不適 */
@media (prefers-reduced-motion: reduce) {
  .cui-modal-overlay, .cui-modal { animation: none; }
}
.cui-modal {
  background: var(--cui-bg); border-radius: var(--cui-radius-xl); box-shadow: var(--cui-shadow-lg);
  width: 100%; max-width: 480px; max-height: calc(100vh - 32px); display: flex; flex-direction: column;
  color: var(--cui-text);
  animation: cui-modal-in .15s ease-out;
}
.cui-modal:focus { outline: none; }   /* tabIndex=-1 只為程式聚焦，不該畫外框 */
.cui-modal-body { line-height: 1.6; } /* 警語是段落式文字，需要行高 */

/* ---------- ConfirmDialog ---------- */
.cui-confirm { display: flex; gap: var(--cui-space-3); align-items: flex-start; }
.cui-confirm-body { flex: 1; min-width: 0; }
.cui-confirm-body p { margin: 0 0 var(--cui-space-2); }
.cui-confirm-body p:last-child { margin-bottom: 0; }
.cui-confirm-icon {
  flex-shrink: 0; width: 40px; height: 40px; border-radius: var(--cui-radius-full);
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 18px; font-weight: 700; line-height: 1;
}
.cui-confirm-medium .cui-confirm-icon { background: var(--cui-warning-bg); color: var(--cui-warning); }
.cui-confirm-high   .cui-confirm-icon { background: var(--cui-danger-bg);  color: var(--cui-danger); }
.cui-confirm-typing { display: flex; flex-direction: column; gap: var(--cui-space-1); margin-top: var(--cui-space-3); font-size: var(--cui-text-sm); }
.cui-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 14px var(--cui-space-4); border-bottom: 1px solid var(--cui-border); }
/* 15px/600 → 18px/700：拉開與內文的層級差，且避開 Windows 中文的 600 字重問題 */
.cui-modal-title { font-size: var(--cui-text-xl); font-weight: 700; margin: 0; }
.cui-modal-body { padding: var(--cui-space-4); overflow-y: auto; font-size: var(--cui-text-base); }
.cui-modal-footer { display: flex; justify-content: flex-end; gap: var(--cui-space-2); padding: var(--cui-space-3) var(--cui-space-4); border-top: 1px solid var(--cui-border); }
.cui-modal-close { background: transparent; border: none; cursor: pointer; font-size: 16px; line-height: 1; color: var(--cui-text-muted); padding: var(--cui-space-1); border-radius: var(--cui-radius-sm); }
.cui-modal-close:hover { background: var(--cui-bg-muted); }

/* ---------- Toast ---------- */
.cui-toast-viewport {
  position: fixed; bottom: var(--cui-space-4); right: var(--cui-space-4); display: flex; flex-direction: column; gap: var(--cui-space-2); z-index: 2000;
  max-width: 360px;
  /* 空容器不可攔截點擊：兩個 viewport 疊在同一位置，上層若吃掉滑鼠事件，
     下層的 toast 就按不到關閉鈕。容器不收事件、toast 本體再收回來。 */
  pointer-events: none;
}
/*
  M7（B3 稽核，低）：底邊偏移統一走規範值 --cui-space-4（16px），與 .cui-toast-viewport
  同一顆 token，兩個容器貼齊同一條底線；先前這裡疊了 bottom:auto 又立刻覆寫回
  var(--cui-space-4)，是清掉舊版位移實驗殘留的死宣告，值從未真的變過 10px，
  這裡把它寫乾淨，避免下一個人以為 auto 還有作用。
  assertive 容器疊在 polite 之上共用同一位置：實務上兩種同時出現的機率低，
  真的同時出現時失敗訊息（assertive）在最上層（z-index 相同、DOM 序在後）。
*/
.cui-toast-viewport-assertive { bottom: var(--cui-space-4); }
.cui-toast {
  display: flex; align-items: flex-start; gap: var(--cui-space-2); padding: 10px var(--cui-space-3); border-radius: var(--cui-radius-lg);
  font-size: var(--cui-text-base); box-shadow: var(--cui-shadow-lg); background: var(--cui-bg); color: var(--cui-text);
  border: 1px solid var(--cui-border); animation: cui-toast-in .15s ease-out;
  pointer-events: auto;
}
.cui-toast-msg { flex: 1; min-width: 0; word-break: break-word; }
/*
  語意圖示：形狀本身帶語意，不依賴色覺（比照 Badge 的形狀圓點做法）。
  ✓ 成功／✕ 失敗／! 警告／i 資訊，各自套該 variant 的語意色。
*/
.cui-toast-icon {
  flex-shrink: 0; width: 18px; height: 18px; border-radius: var(--cui-radius-full);
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; line-height: 1; margin-top: 1px;
}
.cui-toast-success .cui-toast-icon { background: var(--cui-success-bg); color: var(--cui-success); }
.cui-toast-danger  .cui-toast-icon { background: var(--cui-danger-bg);  color: var(--cui-danger); }
.cui-toast-warning .cui-toast-icon { background: var(--cui-warning-bg); color: var(--cui-warning); }
.cui-toast-info    .cui-toast-icon { background: var(--cui-info-bg);    color: var(--cui-info); }
@keyframes cui-toast-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
.cui-toast-success { border-left: 3px solid var(--cui-success); }
.cui-toast-danger { border-left: 3px solid var(--cui-danger); }
.cui-toast-warning { border-left: 3px solid var(--cui-warning); }
.cui-toast-info { border-left: 3px solid var(--cui-info); }
.cui-toast-close { margin-left: auto; background: none; border: none; cursor: pointer; color: var(--cui-text-subtle); font-size: var(--cui-text-base); }

/* ---------- Alert（頁內常駐訊息 / Banner）----------
 *
 * 刻意不新增任何顏色 token：規範 §4 已實測四組語意色在
 * 「淺色/深色 × 語意底色/卡片白」四種疊色下全數通過 WCAG AA，
 * 直接沿用 --cui-{success,danger,warning,info}(-bg) 即可。
 *
 * 疊色實測（文字色 on 語意底色，取自規範 §4 表）：
 *   success 4.57:1（淺）/ 9.38:1（深）   danger  5.91:1 / 6.35:1
 *   warning 6.84:1（淺）/ 9.77:1（深）   info    4.75:1 / 6.49:1
 * ⚠️ 淺色 info 的 4.75:1 貼近 4.5 門檻，日後若有人想把 --cui-info-bg 再調淡，
 *    必須連這裡一起重測。
 *
 * 內文用 --cui-text 而非語意色：常駐訊息往往是兩三行完整句子，整段染紅/染綠
 * 會讓可讀性下降，且語意已由左側色條＋圖示承載。語意色只用在圖示與標題。
 */
.cui-alert {
  display: flex; align-items: flex-start; gap: var(--cui-space-2);
  padding: 10px var(--cui-space-3); border-radius: var(--cui-radius-lg);
  font-size: var(--cui-text-base); line-height: 1.6; color: var(--cui-text);
  border: 1px solid var(--cui-border);
}
.cui-alert-body { flex: 1; min-width: 0; word-break: break-word; }
.cui-alert-title { display: block; font-weight: 700; margin-bottom: 2px; }
.cui-alert-msg > p:first-child { margin-top: 0; }
.cui-alert-msg > p:last-child { margin-bottom: 0; }
/* 圖示尺寸與 Toast 一致（18px 圓形），全站語意徽記長得一樣 */
.cui-alert-icon {
  flex-shrink: 0; width: 18px; height: 18px; border-radius: var(--cui-radius-full);
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; line-height: 1; margin-top: 3px;
  background: var(--cui-bg);
}
.cui-alert-action { flex-shrink: 0; display: flex; align-items: center; gap: var(--cui-space-2); }
/* text-secondary 而非 text-muted：muted(#64748b) 疊在 --cui-warning-bg 上只有約 4.4:1 */
.cui-alert-close {
  flex-shrink: 0; background: none; border: none; cursor: pointer; padding: 0 2px;
  color: var(--cui-text-secondary); font-size: var(--cui-text-base); font-family: var(--cui-font-sans);
  line-height: 1; margin-top: 3px;
}
.cui-alert-close:hover { color: var(--cui-text); }
.cui-alert-success { background: var(--cui-success-bg); border-left: 3px solid var(--cui-success); }
.cui-alert-danger  { background: var(--cui-danger-bg);  border-left: 3px solid var(--cui-danger); }
.cui-alert-warning { background: var(--cui-warning-bg); border-left: 3px solid var(--cui-warning); }
.cui-alert-info    { background: var(--cui-info-bg);    border-left: 3px solid var(--cui-info); }
.cui-alert-success .cui-alert-icon, .cui-alert-success .cui-alert-title { color: var(--cui-success); }
.cui-alert-danger  .cui-alert-icon, .cui-alert-danger  .cui-alert-title { color: var(--cui-danger); }
.cui-alert-warning .cui-alert-icon, .cui-alert-warning .cui-alert-title { color: var(--cui-warning); }
.cui-alert-info    .cui-alert-icon, .cui-alert-info    .cui-alert-title { color: var(--cui-info); }

/* ---------- FormFieldError（欄位級錯誤）----------
 *
 * 色票是 --cui-danger（文字用紅），不是 --cui-danger-solid（實心底配白字專用）。
 * 兩者在淺色下同值 #b91c1c 容易誤以為可互換，但深色下 --cui-danger 亮到
 * #f87171（對卡片底 6.24:1 ✓），solid 則留在 #dc2626（對卡片底 2.67:1 ✗）。
 * 輸入框的紅框沿用既有的 .cui-input-invalid，不另造第二套。
 */
.cui-field-error {
  display: flex; align-items: flex-start; gap: var(--cui-space-1);
  margin-top: var(--cui-space-1); color: var(--cui-danger); font-size: var(--cui-text-sm); line-height: 1.5;
}
.cui-field-error-icon {
  flex-shrink: 0; width: 14px; height: 14px; border-radius: var(--cui-radius-full);
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 10px; font-weight: 700; line-height: 1; margin-top: 1px;
  border: 1px solid var(--cui-danger);
}
.cui-field-error-msg { min-width: 0; word-break: break-word; }

/* ---------- AppShell ---------- */
.cui-shell { display: flex; min-height: 100vh; background: var(--cui-bg-subtle); color: var(--cui-text); }
.cui-shell-sidebar {
  width: 260px; flex-shrink: 0; background: var(--cui-sidebar-bg); color: var(--cui-sidebar-text);
  border-right: 1px solid var(--cui-sidebar-border);
  display: flex; flex-direction: column; transition: width .15s ease;
  /*
   * 深色模式下側欄（暖黑 #100d09）與內容區（冷深藍灰 #0d1117）的明度幾乎相同
   * ——實測對比 1.07:1，邊框線疊色後也只有 1.12:1，等於沒有邊界。純調色值救不了
   * （試過各種深淺，天花板約 1.3:1）。改用縱深陰影建立層次：陰影不吃背景明度差，
   * 是深色介面唯一可靠的分層手段。淺色模式維持原本的邊框線即可，故用變數控制。
   */
  box-shadow: var(--cui-sidebar-shadow, none);
}
.cui-shell-sidebar.cui-collapsed { width: 64px; }
.cui-shell-brand {
  display: flex; align-items: center; gap: 10px; padding: 0 var(--cui-space-4); height: 56px; flex-shrink: 0;
  font-weight: 700; font-size: var(--cui-text-lg); color: var(--cui-sidebar-brand-text);
  border-bottom: 1px solid var(--cui-sidebar-border); white-space: nowrap; overflow: hidden;
}
/* 品牌標記：金色漸層圓角方塊。側欄收合成 64px 時，它是唯一留下的品牌識別。 */
.cui-shell-brand-mark {
  width: 28px; height: 28px; flex-shrink: 0; border-radius: var(--cui-radius-lg);
  background: linear-gradient(135deg, var(--cui-brand-400), var(--cui-brand-700));
  display: flex; align-items: center; justify-content: center;
  color: #17130e; font-weight: 700; font-size: var(--cui-text-lg); line-height: 1;
}
.cui-shell-sidebar.cui-collapsed .cui-shell-brand { justify-content: center; padding: 0; }
.cui-shell-sidebar.cui-collapsed .cui-shell-brand > *:not(.cui-shell-brand-mark) { display: none; }

.cui-shell-nav { flex: 1; overflow-y: auto; padding: 0 var(--cui-space-2) var(--cui-space-4); }
.cui-shell-nav::-webkit-scrollbar { width: 6px; }
.cui-shell-nav::-webkit-scrollbar-track { background: transparent; }
.cui-shell-nav::-webkit-scrollbar-thumb { background: rgba(255,255,255,.15); border-radius: 3px; }

/* 選單搜尋 */
.cui-nav-search-wrap { position: relative; padding: var(--cui-space-3) var(--cui-space-3) var(--cui-space-2); }
.cui-nav-search {
  width: 100%; height: 34px; border-radius: var(--cui-radius-lg);
  /* 邊框 0.09 實測對側欄底只有 1.27:1（深色 1.24），達不到 1.4.11 的 3:1；
     0.40 實測淺 3.83:1／深 3.80:1。填色維持 0.05——1.4.11 要求的是**邊界**可辨識，
     底色本來就該低調，邊框夠了就不必再把整塊提亮。 */
  /*
   * C11（B3 稽核，中）：填色 0.05 疊在側欄底 #17130e 上合成約 #231f1a，
   * placeholder（--cui-sidebar-text-muted #8a8272）對它只剩 4.31:1，不到 AA。
   * 降到 0.02（合成約 #1c1813）→ 4.65:1 ✓；:focus 的 0.08 合成更亮、只剩 4.26:1，
   * 一併降到 0.04（合成約 #1f1911）→ 4.57:1 ✓。
   */
  border: 1px solid rgba(255,255,255,.40); background: rgba(255,255,255,.02);
  color: var(--cui-sidebar-text); padding: 0 var(--cui-space-3) 0 30px;
  font-size: 12.5px; font-family: var(--cui-font-sans);
}
.cui-nav-search::placeholder { color: var(--cui-sidebar-text-muted); }
.cui-nav-search:focus { outline: none; border-color: rgba(214,175,98,.5); background: rgba(214,175,98,.04); }
.cui-nav-search-icon { position: absolute; left: 23px; top: 21px; font-size: var(--cui-text-sm); color: var(--cui-sidebar-text-muted); pointer-events: none; }
.cui-nav-search-hits { font-size: var(--cui-text-xs); color: var(--cui-sidebar-text-muted); padding: var(--cui-space-2) 10px var(--cui-space-1); }
/* 搜尋命中高亮：只在渲染時包一層 <mark>，不改動任何詞條文字 */
.cui-nav-mark { background: rgba(214,175,98,.3); color: var(--cui-sidebar-mark-text); border-radius: 2px; padding: 0 1px; }

/* 分區標題（☆ 常用 / 全部功能） */
.cui-nav-section {
  font-size: var(--cui-text-xs); font-weight: 500; color: var(--cui-sidebar-text-muted);
  letter-spacing: .02em; padding: var(--cui-space-3) var(--cui-space-2) var(--cui-space-1);
}

/* 群組（手風琴）*/
.cui-nav-group { border-radius: var(--cui-radius-md); margin-bottom: var(--cui-space-1); }
.cui-nav-group-header {
  display: flex; align-items: center; gap: var(--cui-space-2); width: 100%; height: 40px;
  padding: 0 var(--cui-space-2); border: none; background: transparent; cursor: pointer;
  border-radius: var(--cui-radius-md); font-size: var(--cui-text-base); font-weight: 700;
  color: var(--cui-sidebar-text); font-family: var(--cui-font-sans); text-align: left;
}
.cui-nav-group-header:hover { background: var(--cui-sidebar-hover-bg); }
.cui-nav-group-header:focus-visible { outline: 2px solid var(--cui-sidebar-active-border); outline-offset: -2px; }
.cui-nav-group.cui-open > .cui-nav-group-header { color: var(--cui-sidebar-brand-text); }
.cui-nav-group-icon { width: 20px; text-align: center; font-size: var(--cui-text-md); opacity: .85; flex-shrink: 0; }
/*
 * 群組標題文字。原本有 text-transform: uppercase —— 對中文完全無效（純冗餘規則），
 * 已移除，改用 letter-spacing 做視覺區隔。
 */
.cui-nav-group-label { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; letter-spacing: .02em; }
.cui-nav-group-caret { font-size: 9px; color: var(--cui-sidebar-text-muted); transition: transform .15s; flex-shrink: 0; }
.cui-nav-group.cui-open > .cui-nav-group-header .cui-nav-group-caret { transform: rotate(90deg); }
.cui-nav-group-items { padding: var(--cui-space-1) 0 var(--cui-space-2); }

/* 項目 */
.cui-nav-item {
  display: flex; align-items: center; gap: var(--cui-space-2); width: 100%; height: 36px; text-align: left;
  border: none; background: transparent; padding: 0 var(--cui-space-2) 0 42px;
  border-radius: var(--cui-radius-md); font-size: var(--cui-text-base); font-weight: 400;
  color: var(--cui-sidebar-text); cursor: pointer; text-decoration: none;
  border-left: 3px solid transparent; font-family: var(--cui-font-sans);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.cui-nav-item:hover { background: var(--cui-sidebar-hover-bg); }
.cui-nav-item.cui-active {
  background: var(--cui-sidebar-active-bg); color: var(--cui-sidebar-active-text);
  border-left-color: var(--cui-sidebar-active-border); font-weight: 500;
}
.cui-nav-item:focus-visible { outline: 2px solid var(--cui-sidebar-active-border); outline-offset: -2px; }
/* 巢狀子群組（如「3. 後端-權限管理」下的權限列表/群組/角色/用户列表）*/
.cui-nav-children { padding-left: var(--cui-space-4); display: flex; flex-direction: column; gap: 1px; border-left: 1px solid var(--cui-sidebar-border); margin-left: 46px; }
.cui-nav-children .cui-nav-item { padding-left: var(--cui-space-3); font-size: 12.5px; }
.cui-nav-toggle { margin-left: auto; font-size: 10px; color: var(--cui-sidebar-text-muted); }

/* 常用釘選 */
.cui-nav-pin {
  display: flex; align-items: center; gap: 6px; height: 32px; padding: 0 var(--cui-space-2);
  border-radius: var(--cui-radius-md); font-size: 12.5px; color: var(--cui-sidebar-text);
  text-decoration: none; cursor: pointer;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.cui-nav-pin:hover { background: var(--cui-sidebar-hover-bg); }
.cui-nav-pin-star { color: var(--cui-brand-500); font-size: var(--cui-text-xs); flex-shrink: 0; }
/* 連結 + 釘選鈕的並排列（釘選鈕是連結的兄弟節點，不可巢狀在 <a> 內）*/
.cui-nav-item-row { display: flex; align-items: center; position: relative; }
.cui-nav-item-row > .cui-nav-item, .cui-nav-item-row > .cui-nav-pin { flex: 1; min-width: 0; }
.cui-nav-pin-btn {
  position: absolute; right: var(--cui-space-2); background: none; border: none; cursor: pointer;
  padding: 2px 4px; line-height: 1; border-radius: var(--cui-radius-sm);
  color: var(--cui-sidebar-text-muted); font-size: var(--cui-text-sm); opacity: 0; flex-shrink: 0;
}
.cui-nav-item-row:hover .cui-nav-pin-btn { opacity: 1; }
.cui-nav-pin-btn:hover { color: var(--cui-brand-400); background: rgba(255,255,255,.06); }
.cui-nav-pin-btn:focus-visible { opacity: 1; outline: 2px solid var(--cui-sidebar-active-border); outline-offset: -2px; }
.cui-nav-pin-btn.cui-pinned { opacity: 1; color: var(--cui-brand-500); }
/* 釘選鈕會蓋住文字尾端，右側預留空間避免長詞條被壓到 */
.cui-nav-item-row:hover > .cui-nav-item, .cui-nav-item-row:hover > .cui-nav-pin { padding-right: 26px; }

/* 收合態：64px icon rail，hover 圖示時該群組完整清單向右浮出 */
.cui-shell-sidebar.cui-collapsed .cui-nav-search-wrap,
.cui-shell-sidebar.cui-collapsed .cui-nav-section,
.cui-shell-sidebar.cui-collapsed .cui-nav-group-label,
.cui-shell-sidebar.cui-collapsed .cui-nav-group-caret,
.cui-shell-sidebar.cui-collapsed .cui-nav-group-items,
.cui-shell-sidebar.cui-collapsed .cui-nav-pin { display: none; }
.cui-shell-sidebar.cui-collapsed .cui-shell-nav { padding: var(--cui-space-2) 0; }
.cui-shell-sidebar.cui-collapsed .cui-nav-group { position: relative; }
.cui-shell-sidebar.cui-collapsed .cui-nav-group-header { justify-content: center; height: 40px; width: 40px; margin: 0 auto var(--cui-space-1); padding: 0; }
.cui-shell-sidebar.cui-collapsed .cui-nav-group.cui-open > .cui-nav-group-header { background: var(--cui-sidebar-active-bg); box-shadow: inset 3px 0 0 var(--cui-sidebar-active-border); }
.cui-nav-flyout {
  position: absolute; left: 100%; top: 0; min-width: 220px; z-index: 900;
  background: #1f1a13; border: 1px solid var(--cui-sidebar-border);
  border-radius: 0 var(--cui-radius-xl) var(--cui-radius-xl) 0;
  box-shadow: var(--cui-shadow-lg); padding: 10px var(--cui-space-2);
}
.cui-nav-flyout-title { font-size: var(--cui-text-sm); font-weight: 700; color: var(--cui-sidebar-brand-text); padding: 6px 10px var(--cui-space-2); }
.cui-nav-flyout .cui-nav-item { padding-left: 10px; height: 32px; font-size: 12.5px; }

/* Topbar */
.cui-shell-topbar {
  height: 56px; flex-shrink: 0; display: flex; align-items: center; justify-content: space-between;
  padding: 0 var(--cui-space-6); border-bottom: 1px solid var(--cui-border); background: var(--cui-bg);
  box-shadow: 0 1px 2px rgba(0,0,0,.04);
}
/*
 * B2 第 2 輪②UIUX（高-1）：實測發現把 .cui-shell-user 縮到 0 還不夠——右側
 * topbarActions（個人資料／明暗切換／登出）三顆按鈕本身也是不可斷詞內容，跟
 * .cui-shell-user 搶同一段窄到只剩 ~140px 的空間，一樣沒有可縮的地方，一樣會把
 * document.documentElement.scrollWidth 撐大（實測 390 寬仍多 50–62px）。這三顆鈕的
 * 組成在 admin/AdminShell.tsx（消費端，不在本包職權內、也不可動），本包能做的是讓
 * 「容納它們的容器」在窄寬時允許換行，而不是用不存在的空間硬擠成一行、溢出到頁面
 * 右邊界。<=480px 讓 Topbar 右側區塊改允許換行（右對齊、自行長高），Topbar 高度
 * 跟著改 auto/min-height，不再固定 56px 卡死第二行。
 */
@media (max-width: 480px) {
  .cui-shell-topbar { height: auto; min-height: 56px; padding-top: 8px; padding-bottom: 8px; row-gap: 6px; }
  .cui-shell-topbar > div:last-child { flex-wrap: wrap; justify-content: flex-end; row-gap: 6px; }
}
.cui-shell-main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
/* max-width 1600 置中：超寬螢幕不再把短表格攤平在整片空白裡 */
.cui-shell-content { flex: 1; padding: var(--cui-space-6) 28px; overflow: auto; }
.cui-shell-content > * { max-width: 1600px; margin-left: auto; margin-right: auto; }
.cui-shell-collapse-btn { background: transparent; border: 1px solid var(--cui-border); border-radius: var(--cui-radius-md); cursor: pointer; padding: var(--cui-space-1) var(--cui-space-2); color: var(--cui-text-muted); font-size: var(--cui-text-sm); }
.cui-shell-collapse-btn:hover { background: var(--cui-bg-muted); }

/*
 * B2 瀏覽器稽核（高）：390 寬所有頁面載入時橫向捲動（scrollWidth 557–562 vs 390）
 * ——側欄在行動寬度預設展開（260px），把 .cui-shell 這條 flex row 撐爆。
 * 規範 §6.4／後台UI視覺翻新規範.md：<768px（沿用 Tailwind 預設的 md 斷點）由
 * AppShell.tsx 預設收合（64px icon rail，維持在原本的 flex 排版內，本來就不會
 * 溢出）；使用者按 ☰ 展開時，這裡把展開態的側欄切成 position:fixed 覆蓋層
 * ——不再佔用 flex row 的寬度，行動寬度下不論展開/收合都不會橫向捲動。
 * 桌機（>=768）不受影響：側欄依然用原本的 width 推擠 .cui-shell-main。
 */
.cui-shell-backdrop { display: none; }
@media (max-width: 767px) {
  /*
   * top: 56px（= .cui-shell-topbar 的高度，見上方）而非 0：蓋版面全高會連 ☰ 鈕
   * 自己都蓋住——展開後使用者再也點不到同一顆鈕收合，只能改點背板。讓覆蓋層從
   * Topbar 下緣開始，Topbar（含 ☰）永遠留在最上層、永遠點得到。
   */
  .cui-shell-sidebar:not(.cui-collapsed) {
    position: fixed; top: 56px; left: 0; height: calc(100vh - 56px); z-index: 1000;
  }
  .cui-shell-sidebar:not(.cui-collapsed) + .cui-shell-backdrop {
    display: block; position: fixed; top: 56px; left: 0; right: 0; bottom: 0; z-index: 999;
    background: rgba(0, 0, 0, .45);
  }
}
/* 明暗切換鈕（頂欄）。三態循環：跟隨系統 → 淺色 → 深色。 */
.cui-theme-toggle {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  width: 32px; height: 32px; padding: 0; flex-shrink: 0;
  background: transparent; border: 1px solid var(--cui-border);
  border-radius: var(--cui-radius-md); cursor: pointer;
  color: var(--cui-text-secondary); font-size: var(--cui-text-md); line-height: 1;
  font-family: var(--cui-font-sans);
}
.cui-theme-toggle:hover { background: var(--cui-bg-muted); color: var(--cui-text); }
.cui-theme-toggle:focus-visible { outline: 2px solid var(--cui-focus-ring); outline-offset: 1px; }

.cui-shell-crumb { font-size: var(--cui-text-sm); font-weight: 400; color: var(--cui-text-muted); }
/*
 * B2 第 2 輪②UIUX（高-1）：390 寬「真實載入」（非桌機 resize）仍橫向捲動 65–66px
 * ——F6（第 1 輪）只處理了側欄，Topbar 本身沒有 overflow 容器（不像
 * .cui-shell-content 有 overflow:auto 會就地吞掉子層溢出），帳號區的姓名／角色是
 * 不可斷詞字串（帳號常無空格），flex item 預設 min-width:auto 讓它們照 max-content
 * 撐開，直接把 document.documentElement.scrollWidth 撐大到 456。
 * 修法：整條鏈給 min-width:0 讓 flex 可縮到 0，姓名/角色改 ellipsis 截斷而非硬撐；
 * <480px 再把可視寬度收更窄——寧可縮名也不准橫向捲動，頭像永遠留著。
 * 規範 §6.4 補述見 後台UI視覺翻新規範.md。
 */
.cui-shell-topbar > div { min-width: 0; }
.cui-shell-user { display: flex; align-items: center; gap: var(--cui-space-2); font-size: 12.5px; min-width: 0; }
.cui-shell-avatar { width: 28px; height: 28px; border-radius: var(--cui-radius-full); background: var(--cui-primary); color: var(--cui-primary-contrast); display: flex; align-items: center; justify-content: center; font-size: var(--cui-text-sm); font-weight: 700; flex-shrink: 0; }
.cui-shell-user-meta { display: flex; flex-direction: column; line-height: 1.25; min-width: 0; overflow: hidden; }
.cui-shell-user-name, .cui-shell-user-role { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%; }
.cui-shell-user-role { color: var(--cui-text-muted); font-size: 11.5px; }
@media (max-width: 480px) {
  .cui-shell-user-meta { max-width: 96px; }
}

/* ---------- PageHeader ---------- */
.cui-page-header { margin-bottom: var(--cui-space-5); }
.cui-page-breadcrumb { font-size: var(--cui-text-sm); color: var(--cui-text-muted); margin-bottom: var(--cui-space-1); }
.cui-page-header-row { display: flex; align-items: center; gap: var(--cui-space-3); flex-wrap: wrap; }
.cui-page-title { font-size: var(--cui-text-2xl); font-weight: 700; line-height: 28px; margin: 0; }
.cui-page-actions { margin-left: auto; display: flex; align-items: center; gap: var(--cui-space-3); }
.cui-page-subtitle { font-size: var(--cui-text-base); color: var(--cui-text-muted); margin: var(--cui-space-1) 0 0; max-width: 68ch; }
.cui-page-header-line { height: 1px; background: var(--cui-border); margin-top: var(--cui-space-4); }

/* 表單卡片：與清單頁的 .cui-dt-card 同一套視覺語言（白底、圓角、陰影、內距） */
.cui-form-card {
  background: var(--cui-bg); border: 1px solid var(--cui-border);
  border-radius: var(--cui-radius-xl); box-shadow: var(--cui-shadow);
  padding: var(--cui-space-5);
}

/* ---------- Login layout ---------- */
.cui-login-wrap {
  min-height: 100vh; display: flex; align-items: center; justify-content: center;
  background: linear-gradient(160deg, #1c1710 0%, #17130e 45%, #100d09 100%);
  padding: var(--cui-space-4);
}
.cui-login-inner { width: 100%; max-width: 380px; }
.cui-login-lockup { text-align: center; margin-bottom: 26px; }
.cui-login-mark {
  width: 56px; height: 56px; border-radius: var(--cui-radius-xl); margin: 0 auto 14px;
  background: linear-gradient(135deg, var(--cui-brand-400), var(--cui-brand-700));
  display: flex; align-items: center; justify-content: center;
  color: #17130e; font-size: 26px; font-weight: 700;
  box-shadow: 0 6px 20px rgba(214,175,98,.28);
}
.cui-login-brand { font-size: var(--cui-text-2xl); font-weight: 700; color: #fff; letter-spacing: .03em; margin: 0; }
.cui-login-tagline { font-size: var(--cui-text-base); color: var(--cui-sidebar-text-muted); margin: 5px 0 0; }
.cui-login-card { width: 100%; background: var(--cui-bg); border-radius: 16px; box-shadow: var(--cui-shadow-lg); padding: var(--cui-space-8) 28px; color: var(--cui-text); }
.cui-login-title { font-size: var(--cui-text-lg); font-weight: 500; margin: 0 0 var(--cui-space-5); }
.cui-login-subtitle { font-size: var(--cui-text-base); color: var(--cui-text-muted); margin: 0 0 var(--cui-space-5); }
.cui-login-footer { text-align: center; margin-top: 22px; font-size: 11.5px; color: #6b6355; }

/* ---------- DataTable ---------- */
.cui-dt-wrap { display: flex; flex-direction: column; color: var(--cui-text); font-size: var(--cui-text-base); }
/* 內容卡片化：搜尋列＋表格＋分頁包成一張白卡，短表格不再浮在空白裡 */
.cui-dt-card { position: relative; background: var(--cui-bg); border: 1px solid var(--cui-border); border-radius: var(--cui-radius-xl); box-shadow: var(--cui-shadow); overflow: hidden; }
.cui-dt-toolbar { display: flex; align-items: center; gap: var(--cui-space-2); flex-wrap: wrap; padding: var(--cui-space-4) var(--cui-space-5); border-bottom: 1px solid var(--cui-border); }
.cui-dt-toolbar-left { display: flex; align-items: center; gap: var(--cui-space-2); flex-wrap: wrap; }
.cui-dt-toolbar-right { margin-left: auto; display: flex; align-items: center; gap: var(--cui-space-1); }
.cui-dt-search-wrap { position: relative; display: inline-flex; align-items: center; }
.cui-dt-search-icon { position: absolute; left: 10px; font-size: var(--cui-text-sm); color: var(--cui-text-subtle); pointer-events: none; }
.cui-dt-search-wrap .cui-dt-search { padding-left: 30px; }
.cui-dt-search { padding: 6px 10px; border: 1px solid var(--cui-border-control); border-radius: var(--cui-radius-sm); font-size: var(--cui-text-base); min-width: 260px; background: var(--cui-bg); color: var(--cui-text); font-family: var(--cui-font-sans); }
.cui-dt-search:focus { outline: none; border-color: var(--cui-primary); box-shadow: 0 0 0 3px var(--cui-focus-ring); }
/*
 * C2（B3 第 3 輪②）：.cui-dt-search／.cui-dt-filter-input 沒有各自的 ::placeholder
 * 規則（不吃 .cui-input，是獨立 class），落到 Tailwind v4 preflight 預設的
 * color-mix(in oklab, currentColor 50%, transparent)——實測淺 3.41:1／深 4.49:1，
 * 都不到 AA 4.5:1。比照 .cui-input::placeholder（見本檔 C3 註解）明講成
 * text-muted：對白 4.76:1（淺）／對深色卡片底 6.73:1（深）✓。
 */
.cui-dt-search::placeholder, .cui-dt-filter-input::placeholder { color: var(--cui-text-muted); }
.cui-dt-scroll { overflow-x: auto; background: var(--cui-bg); }
.cui-dt-table { width: 100%; border-collapse: collapse; font-size: var(--cui-text-base); }
.cui-dt-table th, .cui-dt-table td { padding: var(--cui-space-2) 10px; text-align: left; border-bottom: 1px solid var(--cui-border); vertical-align: middle; }
/*
 * 三階色彩：表頭（最深 + 2px 粗底線）→ 篩選列（淺底 + 虛線頂邊）→ 資料列（白/斑馬）。
 * 原本表頭與篩選列幾乎同色階，使用者分不出「哪列是輸入、哪列是資料」。
 */
.cui-dt-table th {
  background: var(--cui-bg-header); font-weight: 700; color: var(--cui-text-secondary);
  font-size: var(--cui-text-sm); white-space: nowrap; position: sticky; top: 0; z-index: 2;
  border-bottom: 2px solid var(--cui-border-strong); height: 36px;
}
.cui-dt-th-btn { display: inline-flex; align-items: center; gap: 4px; background: none; border: none; cursor: pointer; font: inherit; color: inherit; padding: 0; }
/*
 * C1（B3 第 3 輪②）：排序圖示「↕」原吃 text-muted，對表頭底 4.23:1（淺）／
 * 5.61:1（深）——當年是拿非文字 UI 元件的 3:1 下限（WCAG 1.4.11）驗收，這次改按
 * 純文字 ≥4.5:1 複查，淺色不過。規範（後台UI視覺翻新規範.md §11「表頭對比 + 2px
 * 底線」）本來就把表頭對比不足的解法定為「文字改吃 text-secondary」（見本檔
 * .cui-dt-table th），不是把表頭底改白——三階色彩（表頭 > 篩選列 > 資料列）是
 * 刻意設計，改白會抹平表頭與資料列的層次。故排序圖示比照同一解法，改吃
 * text-secondary：對表頭底 6.74:1（淺）／7.82:1（深）✓，兩者都有大量餘裕。
 */
.cui-dt-sort-icon { font-size: 9px; color: var(--cui-text-secondary); }
.cui-dt-sort-icon.cui-active { color: var(--cui-primary); }
/* 不可篩選的欄位在表頭掛小鎖：主動說明「這欄不能篩，不是系統忘了做」 */
.cui-dt-lock-icon { font-size: 10px; opacity: .5; margin-left: 3px; }
.cui-dt-filter-row th {
  background: var(--cui-bg-subtle); padding: 6px 10px;
  border-top: 1px dashed var(--cui-border-strong); border-bottom: 1px solid var(--cui-border);
  position: sticky; top: 36px; z-index: 1;
}
.cui-dt-filter-input, .cui-dt-filter-select {
  width: 100%; font-size: var(--cui-text-sm); padding: var(--cui-space-1) 6px; height: 28px;
  border: 1px solid var(--cui-border-control); border-radius: var(--cui-radius-sm);
  background: var(--cui-bg); color: var(--cui-text); font-family: var(--cui-font-sans);
}
.cui-dt-filter-input:focus, .cui-dt-filter-select:focus { outline: none; border-color: var(--cui-primary); box-shadow: 0 0 0 2px var(--cui-focus-ring); }
.cui-dt-filter-na { display: block; text-align: center; color: var(--cui-text-subtle); font-size: var(--cui-text-xs); }
/* 區間篩選（filterRange）：兩個輸入框並排，欄寬不夠時自動換行而不是溢出表頭。 */
.cui-dt-filter-range { display: flex; gap: 4px; flex-wrap: wrap; min-width: 150px; }
.cui-dt-filter-range > .cui-dt-filter-input { flex: 1 1 68px; min-width: 68px; }
.cui-dt-table.cui-variant-admin tbody tr:nth-child(even) > td { background: var(--cui-stripe); }
.cui-dt-table.cui-variant-admin tbody tr:hover > td { background: var(--cui-brand-50); }
.cui-dt-table.cui-variant-web tbody tr:hover > td { background: var(--cui-stripe); }
/* 密度：緊湊 32px / 舒適 40px（存 localStorage，比照欄位個人化）*/
.cui-dt-table tbody td { height: 40px; }
.cui-dt-table.cui-density-compact tbody td { height: 32px; padding: var(--cui-space-1) 10px; font-size: 12.5px; }
/* 對齊規則：數字/金額/序號靠右並用等寬數字，日期靠左，布林/狀態置中 */
.cui-dt-align-right { text-align: right; font-variant-numeric: tabular-nums; }
.cui-dt-align-center { text-align: center; }
/* datetime 雙行：日期一行、時間小字一行，省寬度又能對齊 */
.cui-dt-datetime-date { display: block; font-size: var(--cui-text-base); line-height: 16px; }
/*
 * 時間小字用 text-muted 而非 text-subtle。text-subtle(#64748b) 對純黑的對比上限
 * 只有 4.41:1 —— 無論背景多暗都到不了 AA 4.5:1，那是該 token 的結構性限制
 * （它本來就註明「僅供 placeholder/disabled，不保證 AA」）。時間是真的要讀的資訊，
 * 不是裝飾，故改吃 text-muted（深色下對背景有 7.48:1 的空間）。
 */
.cui-dt-datetime-time { display: block; font-size: var(--cui-text-xs); color: var(--cui-text-muted); line-height: 14px; }
/* 「建立者/updatedBy」欄（formatters.tsx 的 user formatter）沿用同一顆 muted，理由同上。 */
.cui-dt-cell-user { color: var(--cui-text-muted); }
/*
 * C7（B3 稽核，高）：上面兩顆 muted 文字的說明只驗過「純白/深色卡片底」，斑馬紋
 * 偶數列疊了 --cui-stripe（#f5f7fa）之後，muted 對它只剩 4.43:1，掉到 AA 門檻
 * 以下（深色的 --cui-stripe #1b212c 上 muted 仍有 6.30:1，沒有這個問題）。
 * text-secondary 對 --cui-stripe 有 7.06:1（淺）／對深色 stripe 8.78:1（深），
 * 兩色都過，改在斑馬列裡把這兩顆蓋成 secondary，非斑馬列維持 muted 的次要層級。
 */
.cui-dt-table.cui-variant-admin tbody tr:nth-child(even) .cui-dt-datetime-time,
.cui-dt-table.cui-variant-admin tbody tr:nth-child(even) .cui-dt-cell-user {
  color: var(--cui-text-secondary);
}
/*
 * B2（B3 第 3 輪②，中）：各 admin 頁面自行 inline style={{color:"var(--cui-text-subtle)"}}
 * 標示表格內的次要文字（空值「—」、「不限」類 badge），text-subtle 現值同
 * text-muted（#64748b），對純白 4.76:1 沒問題，但疊在斑馬紋偶數列 --cui-stripe
 * （#f5f7fa）上只剩 4.43:1，掉到 AA 4.5:1 門檻以下，且違反本檔 172 行
 * 「--cui-text-muted 不可疊灰底」的既有規範——text-subtle 現在等同 muted，
 * 同一條限制當然也適用於它。
 *
 * 不比照上面 C7 的「非斑馬列 muted／斑馬列覆寫 secondary」兩階寫法，因為這顆
 * class 給 inline 用法統一取代，沒有理由讓呼叫端還要分斑馬/非斑馬——直接全程
 * 用 text-secondary，兩種底色都有大量餘裕，其他 muted class 的雙階寫法保留是
 * 因為它們原本就要在非斑馬列維持更淡的視覺層級，這顆沒有此需求。
 *
 * 實測（相對亮度公式，四色）：
 *   淺色 text-secondary #475569 對白 #ffffff        7.58:1 ✓
 *   淺色 text-secondary #475569 對 stripe #f5f7fa    7.06:1 ✓
 *   深色 text-secondary #b6c0ce 對卡片底 #161b24     9.39:1 ✓
 *   深色 text-secondary #b6c0ce 對 stripe #1b212c    8.78:1 ✓
 */
.cui-dt-cell-muted { color: var(--cui-text-secondary); }
.cui-dt-table.cui-density-compact .cui-dt-datetime-date { font-size: 12.5px; }
.cui-dt-empty { padding: var(--cui-space-8); text-align: center; color: var(--cui-text-subtle); }
.cui-dt-empty-icon { font-size: var(--cui-space-8); opacity: .3; display: block; margin-bottom: var(--cui-space-3); }
/* max-width + auto margin：空態說明常是完整句子，不設上限會被拉成滿表格寬的一長行 */
.cui-dt-empty-title { font-size: var(--cui-text-md); color: var(--cui-text-secondary); display: block; margin: 0 auto 6px; max-width: 68ch; }
/* display:block 讓兩段式空態（標題 + 說明）能各佔一行，不論兩段是兄弟還是巢狀 */
.cui-dt-empty-hint { font-size: var(--cui-text-sm); color: var(--cui-text-subtle); display: block; margin: 0 auto; max-width: 68ch; }
/* ntext 截斷：影片描述那種長文不再把列高撐得參差不齊 */
.cui-dt-cell-ntext {
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;
  overflow: hidden; max-width: 320px; word-break: break-word; line-height: 1.45;
}
.cui-dt-table.cui-density-compact .cui-dt-cell-ntext { -webkit-line-clamp: 2; }
.cui-dt-cell-truncate { display: block; max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cui-dt-actions { display: flex; gap: var(--cui-space-1); align-items: center; justify-content: flex-end; }
.cui-dt-footer { display: flex; align-items: center; gap: var(--cui-space-3); flex-wrap: wrap; padding: var(--cui-space-3) var(--cui-space-5); border-top: 1px solid var(--cui-border); background: var(--cui-bg); }
.cui-dt-total { color: var(--cui-text-secondary); font-size: 12.5px; }
/*
 * B2 第 2 輪②UIUX（高-1）：同一份 390 寬稽核也點名 .cui-dt-pager —— 數字分頁列
 * （«‹ 頁碼… ›»＋每頁筆數下拉）一長串按鈕不可斷詞，.cui-dt-footer 雖有
 * flex-wrap 讓它自己換到第二行，但那一行本身仍比卡片寬，flex item 預設
 * min-width:auto 讓它照 max-content 撐開；卡片外層 .cui-shell-content 雖有
 * overflow:auto 會把撐出去的部分吞掉不致波及 body scrollWidth，但代價是
 * 使用者得橫向捲動「整個內容區」（連表格一起位移）才能點到頁碼——體驗等於捲動。
 * 修法採規範建議的第二種：min-width:0 讓分頁列可縮，縮不下的部分改在**自己的
 * 容器**內 overflow-x:auto 捲動，不牽動表格／搜尋列的位置。
 */
/*
 * 裁決 23／26 第 1 輪②次要 6：375px 時上面那套「自己捲」的作法雖然不撐破版面，
 * 但捲軸不明顯——畫面上只看得到「20 筆/頁」、«、‹、「1」，第 2 頁以後與末頁捷徑
 * 全藏在看不見的捲動區裡，使用者不會知道還能往右滑。
 * 改為 flex-wrap:wrap 換行排版：所有頁碼一次全露出來，不需要任何捲動提示。
 * ⛔ 不照搬舊站在窄視窗的表現——舊站 Bootstrap 5 的 .pagination 是
 *    display:flex 無 wrap（vendor/bower-asset/bootstrap/dist/css/bootstrap.css:4757），
 *    375px 下整排頁碼直接把版面撐出橫向捲軸，那是 BUG 不是要照搬的行為。
 * overflow-x:auto 留著當保險（極窄視窗連一顆按鈕都放不下時仍捲得到），
 * 換行之後正常情況不會觸發。
 */
.cui-dt-pager { margin-left: auto; display: flex; flex-wrap: wrap; justify-content: flex-end; align-items: center; gap: var(--cui-space-2); min-width: 0; max-width: 100%; overflow-x: auto; scrollbar-width: thin; }
.cui-dt-pager:focus { outline: none; }
/*
 * 「跳到分頁」跳連結：平常視覺隱藏（沿用 .cui-visually-hidden），Tab 聚焦時浮現。
 * ⛔ 不能永遠隱藏——鍵盤使用者聚焦到一個看不見的連結，畫面上焦點就憑空消失了。
 */
.cui-dt-skiplink:focus {
  position: absolute; width: auto; height: auto; margin: 0; overflow: visible;
  clip: auto; z-index: 5; padding: 4px var(--cui-space-3);
  background: var(--cui-bg); color: var(--cui-primary);
  border: 1px solid var(--cui-primary); border-radius: var(--cui-radius-sm); font-size: 12.5px;
}
.cui-dt-page-btn {
  min-width: 28px; height: 28px; padding: 0 var(--cui-space-2); border: 1px solid var(--cui-border); background: var(--cui-bg);
  color: var(--cui-text-secondary); border-radius: var(--cui-radius-sm); cursor: pointer; font-size: 12.5px;
  display: inline-flex; align-items: center; justify-content: center; font-family: var(--cui-font-sans);
}
.cui-dt-page-btn:hover:not(:disabled) { background: var(--cui-bg-muted); }
.cui-dt-page-btn:disabled { color: var(--cui-text-subtle); cursor: not-allowed; opacity: .6; }
.cui-dt-page-btn.cui-active { background: var(--cui-primary); color: var(--cui-primary-contrast); border-color: var(--cui-primary); font-weight: 700; }
.cui-dt-page-ellipsis { color: var(--cui-text-subtle); padding: 0 var(--cui-space-1); font-size: 12.5px; }
/* 總頁數多於點得到的頁數時的說明；整行另起，不與頁碼擠在同一列。 */
.cui-dt-pager-note { flex-basis: 100%; margin: 0; text-align: right; color: var(--cui-text-secondary); font-size: 12px; line-height: 1.6; }
.cui-dt-page-size { border: 1px solid var(--cui-border-control); border-radius: var(--cui-radius-sm); padding: var(--cui-space-1) 6px; height: 28px; font-size: 12.5px; background: var(--cui-bg); color: var(--cui-text); font-family: var(--cui-font-sans); }
.cui-dt-colsettings-list { display: flex; flex-direction: column; gap: 2px; max-height: 320px; overflow-y: auto; }
.cui-dt-colsettings-row { display: flex; align-items: center; gap: var(--cui-space-2); padding: 6px var(--cui-space-2); border-radius: var(--cui-radius-md); }
.cui-dt-colsettings-row:hover { background: var(--cui-bg-muted); }
.cui-dt-colsettings-label { flex: 1; font-size: var(--cui-text-base); }
.cui-dt-colsettings-moves { display: flex; gap: 2px; }
/* 載入中：skeleton 列，表頭與工具列常駐不閃爍 */
.cui-dt-skeleton { height: 12px; border-radius: var(--cui-radius-sm); background: linear-gradient(90deg, var(--cui-bg-muted) 25%, var(--cui-border) 50%, var(--cui-bg-muted) 75%); background-size: 200% 100%; animation: cui-shimmer 1.2s infinite; }
@keyframes cui-shimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }
.cui-dt-error { padding: var(--cui-space-6); text-align: center; }
.cui-dt-error-msg { color: var(--cui-danger); font-size: var(--cui-text-base); display: block; margin-bottom: var(--cui-space-3); }
/*
 * B2 第 4 輪②UIUX 3（低-中）：user-list「匯入使用者」原本用 <label> 包一顆
 * display:none 的 <input type="file">，靠點 label 觸發選檔——<label> 不在鍵盤
 * tab 順序上，鍵盤使用者完全碰不到。改成 <Button> 呼叫 inputRef.current?.click()，
 * input 本身需要仍是「可程式化聚焦／點擊」的真實表單控制項（display:none 的元素
 * 在部分瀏覽器連 ref.click() 都不保證跳出原生選檔視窗），所以不能用
 * display:none，改用這顆標準的「視覺隱藏但仍在無障礙樹與可程式化互動範圍內」
 * 寫法（沿用業界慣用的 visually-hidden／sr-only 技術：1px 尺寸＋clip＋
 * overflow:hidden，不是 width:0 或 visibility:hidden，那兩種在部分瀏覽器一樣
 * 會讓 .click() 選檔視窗不出現）。呼叫端另補 tabIndex={-1}，讓它不會在觸發用
 * Button 之外多一個看不見的 tab 停駐點。
 */
.cui-visually-hidden {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
}

/* ---------- RemoteSelect（遠端搜尋下拉，舊站 kartik Select2 等價）----------
   框線／focus ring 沿用 .cui-input 的同一組 token，讓它與其它表單控制項在同一
   排裡看起來是同一種東西；顏色一律吃 --cui-* token，深色模式自動跟隨。 */
.cui-rs { position: relative; width: 100%; font-size: var(--cui-text-base); }
.cui-rs-control {
  display: flex; flex-wrap: wrap; align-items: center; gap: 4px;
  box-sizing: border-box; min-height: 36px;
  border: 1px solid var(--cui-border-control); border-radius: var(--cui-radius-sm);
  padding: 3px 4px 3px 6px; background: var(--cui-bg); color: var(--cui-text);
}
.cui-rs:focus-within .cui-rs-control {
  border-color: var(--cui-primary);
  box-shadow: 0 0 0 3px var(--cui-focus-ring);
}
.cui-rs-invalid .cui-rs-control { border-color: var(--cui-danger); }
.cui-rs-invalid:focus-within .cui-rs-control {
  border-color: var(--cui-danger); box-shadow: 0 0 0 3px rgba(185, 28, 28, .18);
}
.cui-rs-disabled .cui-rs-control { background: var(--cui-bg-muted); cursor: not-allowed; }
.cui-rs-input {
  flex: 1 1 60px; min-width: 60px;
  border: none; outline: none; background: transparent; color: inherit;
  padding: 4px 2px; font-size: inherit; font-family: var(--cui-font-sans);
}
.cui-rs-input::placeholder { color: var(--cui-text-muted); }
.cui-rs-input:disabled { color: var(--cui-text-subtle); cursor: not-allowed; }
/* 多選 chip：底色用 brand-100，與 badge 一樣是「淡底＋深字」的語彙。 */
.cui-rs-chip {
  display: inline-flex; align-items: center; gap: 4px; max-width: 100%;
  background: var(--cui-brand-100); color: var(--cui-text);
  border: 1px solid var(--cui-brand-300); border-radius: var(--cui-radius-sm);
  padding: 1px 4px 1px 8px; font-size: var(--cui-text-sm); line-height: 1.6;
}
.cui-rs-chip-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cui-rs-chip-remove, .cui-rs-clear {
  background: none; border: none; cursor: pointer; padding: 0 4px;
  color: var(--cui-text-muted); font-size: var(--cui-text-base); line-height: 1;
}
.cui-rs-chip-remove:hover, .cui-rs-clear:hover { color: var(--cui-text); }
.cui-rs-chip-remove:focus-visible, .cui-rs-clear:focus-visible {
  outline: 2px solid var(--cui-focus-ring); outline-offset: 1px; border-radius: var(--cui-radius-sm);
}
.cui-rs-chip-remove:disabled { cursor: not-allowed; opacity: .5; }
.cui-rs-popup {
  position: absolute; z-index: 30; left: 0; right: 0; top: calc(100% + 2px);
  background: var(--cui-bg); border: 1px solid var(--cui-border-strong);
  border-radius: var(--cui-radius-sm); box-shadow: var(--cui-shadow-sm);
  max-height: 260px; overflow-y: auto;
}
.cui-rs-list { list-style: none; margin: 0; padding: 4px 0; }
.cui-rs-option { padding: 6px 10px; cursor: pointer; color: var(--cui-text); }
/* hover 與鍵盤游標共用同一個高亮：滑鼠與鍵盤使用者看到的是同一個「目前這一列」。 */
.cui-rs-option-active { background: var(--cui-brand-100); }
.cui-rs-option-selected { font-weight: 500; }
.cui-rs-option-disabled { color: var(--cui-text-subtle); cursor: not-allowed; }
/* 搜尋中／無資料／請輸入 N 字以上 —— 放在 aria-live 區塊，螢幕閱讀器會朗讀。 */
.cui-rs-status { padding: 8px 10px; color: var(--cui-text-muted); font-size: var(--cui-text-sm); }
`;

export function ensureStylesInjected(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(CUI_STYLE_TAG_ID)) return;
  const el = document.createElement("style");
  el.id = CUI_STYLE_TAG_ID;
  el.textContent = BASE_CSS;
  document.head.appendChild(el);
}
